import React from 'react';
import { UserCircle, ChevronDown } from 'lucide-react';
import toast from 'react-hot-toast';

const getRoleBadge = (user, contextRole = 'Account Manager') => {
  // Get user's roles array
  const roles = user.roles || [user.role];
  
  // Role priority and display mapping
  const roleMap = {
    'account_manager': { text: 'Account Manager', color: 'bg-orange-100 text-orange-800', priority: 4 },
    'lead_account_manager': { text: 'Lead Account Manager', color: 'bg-red-100 text-red-800', priority: 5 },
    'lead_recruiter': { text: 'Lead Recruiter', color: 'bg-blue-100 text-blue-800', priority: 3 },
    'recruiter_supervisor': { text: 'Recruiter Supervisor', color: 'bg-purple-100 text-purple-800', priority: 3 },
    'recruiter': { text: 'Recruiter', color: 'bg-green-100 text-green-800', priority: 2 }
  };
  
  // For Account Manager dashboard context, prioritize account_manager roles
  if (contextRole === 'Account Manager') {
    // Check if user has account_manager or lead_account_manager role
    if (roles.includes('account_manager')) {
      return roleMap['account_manager'];
    }
    if (roles.includes('lead_account_manager')) {
      return roleMap['lead_account_manager'];
    }
  }
  
  // Default: find highest priority role
  let highestRole = null;
  let highestPriority = 0;
  
  for (const role of roles) {
    const roleInfo = roleMap[role];
    if (roleInfo && roleInfo.priority > highestPriority) {
      highestPriority = roleInfo.priority;
      highestRole = roleInfo;
    }
  }
  
  return highestRole || { text: roles[0] || 'User', color: 'bg-gray-100 text-gray-800' };
};

const UserSelector = ({ selectedUser, availableUsers, onUserChange, showDropdown, onToggleDropdown, role = 'Account Manager' }) => {
  const badge = getRoleBadge(selectedUser, role);

  const handleUserSelect = (user) => {
    onUserChange(user);
    onToggleDropdown(false);
    toast.success(`Switched to ${user.full_name}`);
  };

  return (
    <div className="relative">
      <button
        onClick={() => onToggleDropdown(!showDropdown)}
        className="flex items-center gap-3 px-4 py-2 bg-orange-50 border-2 border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <UserCircle className="h-5 w-5 text-orange-600" />
          <div className="text-left">
            <p className="text-sm font-medium text-gray-900">{selectedUser.full_name}</p>
            <p className={`text-xs px-2 py-0.5 rounded ${badge.color}`}>{badge.text}</p>
          </div>
        </div>
        <ChevronDown className="h-4 w-4 text-gray-600" />
      </button>
      
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto z-50">
          <div className="p-2 border-b border-gray-200 bg-gray-50">
            <p className="text-xs font-medium text-gray-600 uppercase px-2">Switch User Context</p>
          </div>
          {availableUsers.map(user => {
            const userBadge = getRoleBadge(user, role);
            return (
              <button
                key={user.id}
                onClick={() => handleUserSelect(user)}
                className={`w-full text-left px-4 py-3 hover:bg-orange-50 border-b last:border-0 transition-colors ${
                  selectedUser.id === user.id ? 'bg-orange-50' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{user.full_name}</p>
                    <p className="text-xs text-gray-600">{user.email}</p>
                    {user.assigned_clients && user.assigned_clients.length > 0 ? (
                      <p className="text-xs text-green-600 mt-1">
                        {user.assigned_clients.length} assigned client{user.assigned_clients.length !== 1 ? 's' : ''}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-400 mt-1">No clients assigned</p>
                    )}
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${userBadge.color}`}>
                    {userBadge.text}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UserSelector;
